import NoData from '@/components/basic/error-pages/no-data';
import Spin from '@/components/basic/spin';
import theme from '@/style';
import React from 'react';
import {ScrollView, View, StyleSheet} from 'react-native';
import LinearGradient from '@/components/basic/linear-gradient/linear-gradient';
import {getIGameUserRanking} from '../result-service';
import {getIGameLastOrder} from '../result-service';
import {IGameOrder, IGameTopItem} from '../result.type';
import Text from '@/components/basic/text';
import ListItem from '../scratch/list-item';
import {currentBrand} from '@/brands';
import LazyImage from '@/components/basic/image';
import {goTo} from '@/utils';
import ResultPanel from '../components/result-panel/result-panel';
import globalStore from '@/services/global.state';
import {useTranslation} from 'react-i18next';
import ShowMoreButton from '../components/show-more-button';

const defaultVisibleCount = 10;

const Casino = () => {
  const [loading, setLoading] = React.useState(true);
  const [list, setList] = React.useState<IGameTopItem[]>([]);
  const [gameOrder, setGameOrder] = React.useState<IGameOrder>();
  const [showMore, setShowMore] = React.useState(false);
  const isAnna = currentBrand.key === 'anna';
  const {t} = useTranslation();

  const getList = async () => {
    try {
      setLoading(true);
      setShowMore(false);
      if (globalStore.token) {
        const [order, ranking] = await Promise.allSettled([
          getIGameLastOrder(2),
          getIGameUserRanking(2),
        ]);
        setGameOrder(order.status === 'fulfilled' ? order.value : undefined);
        setList(ranking.status === 'fulfilled' ? ranking.value || [] : []);
      } else {
        setGameOrder(undefined);
        const ranking = await getIGameUserRanking(2);
        setList(ranking || []);
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    getList();
  }, []);

  const visibleList = React.useMemo(() => {
    return showMore ? list : list.slice(0, defaultVisibleCount);
  }, [list, showMore]);

  return (
    <Spin
      loading={loading}
      style={[theme.flex.flex1, theme.background.lightGrey]}>
      <ScrollView
        contentContainerStyle={[
          isAnna ? styles.annaContent : theme.padding.btml,
          list.length === 0 && styles.emptyContent,
        ]}>
        {!!gameOrder?.orderId && (
          <ResultPanel
            onMore={() => goTo('Bets', {tag: 'Casino'})}
            betTime={gameOrder.orderTime}
            winAmount={
              gameOrder.orderStatus === 1 ? gameOrder.orderWinAmount : undefined
            }>
            <View style={[theme.padding.lrl, theme.padding.tbs]}>
              <View style={[theme.flex.row, theme.flex.centerByCol]}>
                <LazyImage
                  radius={8}
                  imageUrl={gameOrder.gameIcon}
                  width={48}
                  height={48}
                />
                <View
                  style={[
                    styles.orderContent,
                    isAnna && styles.annaOrderContent,
                  ]}>
                  <Text
                    size="medium"
                    fontFamily="fontInterBold"
                    main
                    style={isAnna ? styles.annaOrderTitle : undefined}>
                    {gameOrder.gameName}
                  </Text>
                  <Text
                    accent
                    style={[
                      styles.orderIdText,
                      isAnna && styles.annaOrderIdText,
                    ]}>
                    {t('label.id')}: {gameOrder.orderId}
                  </Text>
                </View>
              </View>
            </View>
          </ResultPanel>
        )}
        {!loading && list.length > 0 && (
          <View
            style={[
              gameOrder?.orderId
                ? styles.rankHeaderWithOrder
                : theme.margin.topl,
              theme.overflow.hidden,
              styles.rankHeaderWrap,
              isAnna && styles.annaRankHeaderWrap,
            ]}>
            <LinearGradient
              colors={['#FFFFFF', '#FFFFFF00']}
              style={[theme.padding.l, theme.flex.row, theme.flex.centerByCol]}>
              <Text
                main
                fontFamily="fontInterBold"
                size="medium"
                style={isAnna ? styles.annaLeaderTitle : undefined}>
                Today's leaderboard
              </Text>
            </LinearGradient>
          </View>
        )}
        {!loading && list.length === 0 ? null : (
          <View style={[styles.tableHeader, isAnna && styles.annaTableHeader]}>
            <View style={[theme.flex.flex1]}>
              <Text style={styles.tableHeaderText}>GAME</Text>
            </View>
            <View style={styles.userCol}>
              <Text textAlign="center" style={styles.tableHeaderText}>
                USER
              </Text>
            </View>
            <View style={styles.userCol}>
              <Text textAlign="right" style={styles.tableHeaderText}>
                BONUS
              </Text>
            </View>
          </View>
        )}
        {!loading && list.length === 0 && (
          <View style={[theme.flex.center, theme.flex.flex1, styles.emptyWrap]}>
            <NoData />
          </View>
        )}
        {visibleList.map((item, _i) => {
          return <ListItem key={_i} {...item} index={_i} startWithGrey />;
        })}
        {!loading && list.length > defaultVisibleCount && !showMore && (
          <View style={styles.moreWrap}>
            <ShowMoreButton
              label={t('home.label.showMore')}
              onPress={() => setShowMore(true)}
              style={styles.moreButton}
            />
          </View>
        )}
      </ScrollView>
    </Spin>
  );
};

const styles = StyleSheet.create({
  annaContent: {
    paddingTop: 12,
    paddingBottom: 72,
  },
  emptyContent: {
    flexGrow: 1,
  },
  rankHeaderWrap: {
    borderTopLeftRadius: theme.paddingSize.s,
    borderTopRightRadius: theme.paddingSize.s,
    marginHorizontal: 12,
  },
  annaRankHeaderWrap: {
    marginTop: 0,
  },
  rankHeaderWithOrder: {
    marginTop: 12,
  },
  annaLeaderTitle: {
    letterSpacing: 0.2,
  },
  orderContent: {
    marginLeft: 12,
    justifyContent: 'center',
  },
  annaOrderContent: {
    rowGap: 4,
  },
  annaOrderTitle: {
    letterSpacing: -0.2,
  },
  orderIdText: {
    marginTop: 4,
    fontSize: 12,
  },
  annaOrderIdText: {
    marginTop: 0,
  },
  tableHeader: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#E9ECF5',
    columnGap: 8,
    flexDirection: 'row',
    marginHorizontal: 12,
  },
  annaTableHeader: {
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  tableHeaderText: {
    fontSize: 11,
    fontWeight: '500',
    color: '#7F8896',
    letterSpacing: 0.2,
  },
  userCol: {
    width: 80,
  },
  emptyWrap: {
    minHeight: 400,
  },
  moreWrap: {
    paddingTop: 8,
    paddingHorizontal: 12,
    paddingBottom: 12,
    marginHorizontal: 12,
    backgroundColor: '#FFFFFF',
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  moreButton: {
    backgroundColor: '#F3F5FA',
  },
});

export default Casino;
